/* eslint-disable prettier/prettier */
import React, { Fragment, useRef, useEffect } from 'react';
import { translate } from '../Services/translation';
import LanguageSelector from './LanguageSelector';

const NavigationItem = ({ links = [], contentLink = null, isSecondLevel = false, navigationInfo = {} }) => {
  const menuRef = useRef(null);

  const { registerUrl, loginUrl, isLoggedIn, logo, hideAuthButtons } = navigationInfo;

  const handleKeyDown = (handler) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler(e);
    }
  };

  const toggleMenu = (e) => {
    e.preventDefault();
    if (!menuRef.current) return;
    menuRef.current.classList.toggle('navbar__menu--show');

    const dropdown = document.getElementById("dropdown");
    const menu = document.getElementById("menu");

    if (dropdown) {
      // Toggle dropdown
      dropdown.classList.toggle('show');

      // Special case: If current element is the menu button, we toggle the child "dropdown" element to skip the first menu item as dropdown and show the child elements instead.
      if (menuRef.current.id === menu.id) {
        dropdown.classList.toggle('navbar__menu--show');
      }
    }
  };

  const resetMenuState = () => {
    const dropdown = document.getElementById("dropdown");
    if (dropdown) {
      dropdown.classList.remove("show");
    }
    const elements = document.getElementsByClassName("navbar__menu");
    for (let i = 0; i < elements.length; i++) {
      elements[i].classList.remove("navbar__menu--show");
    }
  };

  const closeMenu = (e) => {
    e.preventDefault();
    resetMenuState();
  };

  const handleMenuKeyDown = (e) => {
    if (e.key !== 'Tab' || !menuRef.current) return;

    const focusableElements = Array.from(menuRef.current.querySelectorAll(
      'a[href], button, [tabindex="0"], input, select, textarea'
    )).filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  // Close menu when clicking outside the navbar (desktop)
  useEffect(() => {
    if (contentLink) return;

    const handleClickOutside = (e) => {
      const navbar = document.getElementById('navbar');
      if (navbar && !navbar.contains(e.target)) {
        resetMenuState();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contentLink]);

  const additionClass =
    contentLink && contentLink.attributes
      ? contentLink.attributes.cssValue
      : null;
  const selectedClass =
    contentLink && contentLink.isSelected ? 'navbar__link--selected' : '';
  const hasChildrenClass = links.length > 0 ? 'has-children' : null;
  const firstChild = links.find(link => link.url)?.url;
  const hasNameOrChildren = (link) =>
    link.name || (link.links || []).length > 0;
  
  return (
    <Fragment>
      {!contentLink ? (
        <div className='navbar__burger-menu-container' onClick={toggleMenu}>
          <a
            className="navbar__burger-menu-icon"
            rel="nofollow"
            href="#"
            title={translate('general.menu') || 'menu'}
          ></a>
          <div className='navbar__burger-menu-title'>{translate('general.menu')}</div>
        </div>
      ) : (
        <Fragment>
          <a
            className={`navbar__link ${contentLink.isMegaMenu ? 'hidden' : ''} ${selectedClass} ${hasChildrenClass || ''
              } ${additionClass || ''}`}
            href={contentLink.url || firstChild || '#'}
            dangerouslySetInnerHTML={{ __html: contentLink.name }}
          ></a>
          {links.length > 0 && !contentLink.isMegaMenu && (
            <i
              onClick={toggleMenu}
              onKeyDown={handleKeyDown(toggleMenu)}
              tabIndex={0}
              role="button"
              aria-label={translate('general.open') || 'Öppna'}
              className="navbar__icon--caret-right navbar__icon navbar__icon--open"
            ></i>
          )}
        </Fragment>
      )}

      {links.length > 0 && (
        <div className={"navbar__menu"} ref={menuRef} id={isSecondLevel ? "dropdown" : !contentLink ? "menu" : ""} onKeyDown={(!contentLink || isSecondLevel) ? handleMenuKeyDown : undefined}>
          <div className="navbar__menu-header">
            {!contentLink ? (
              <span
                className="navbar__icon navbar__icon--close"
                onClick={closeMenu}
                onKeyDown={handleKeyDown(closeMenu)}
                tabIndex={0}
                role="button"
                aria-label={translate('dropdown.close') || 'Stäng'}
              ></span>
            ) : (
              <Fragment>
                {!isSecondLevel && <i
                  className="navbar__icon--caret-left navbar__icon"
                  onClick={toggleMenu}
                  onKeyDown={handleKeyDown(toggleMenu)}
                  tabIndex={0}
                  role="button"
                  aria-label={translate('general.back') || 'Tillbaka'}
                ></i>}
                {logo && logo.url ? (
                  <img className="navbar__menu-logo" src={logo.url} alt={logo.alt || ''} />
                ) : (
                  <span className="icon-rco-logo" role="img" aria-label="RCO"></span>
                )}
                <div className='navbar__language-selector-mobile'><LanguageSelector /></div>
                <div className='navbar__dropdown-mobile'><span className='navbar__icon--close-container' onClick={closeMenu} onKeyDown={handleKeyDown(closeMenu)} tabIndex={0} role="button" aria-label={translate('dropdown.close') || 'Stäng'}><span className='navbar__icon--close-dropdown'></span></span></div>
              </Fragment>
            )}
          </div>
          {!isLoggedIn && !hideAuthButtons && <div className='navbar__dropdown-button-container'>
            <a className='menu-signup' href={registerUrl}>{translate("dropdown.requestregister")}</a>
            <a className='menu-login' href={loginUrl}>{translate("dropdown.login")}</a>
          </div>}
          {isSecondLevel && <div className='navbar__dropdown-top'><span onClick={closeMenu} onKeyDown={handleKeyDown(closeMenu)} tabIndex={0} role="button" aria-label={translate('dropdown.close') || 'Stäng'}><span className='navbar__icon--close-dropdown'></span><span className='navbar__icon--close-dropdown-text'>{translate("dropdown.close")}</span></span></div>}

          <ul className="navbar__menu-links">
            {links.length > 0 &&
              links.map(
                (link, index) =>
                  hasNameOrChildren(link) && (
                    <li className="navbar__item" key={index}>
                      <NavigationItem links={link.links} contentLink={link} isSecondLevel={link.isMegaMenu} navigationInfo={navigationInfo} />
                    </li>
                  )
              )}
          </ul>
        </div>
      )}
    </Fragment>
  );
};

export default NavigationItem;
